import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';

export type NoticeKind = 'info' | 'error';

interface Notice {
  readonly id: number;
  readonly kind: NoticeKind;
  readonly text: string;
}

type Notify = (text: string, kind?: NoticeKind) => void;

const NoticeContext = createContext<Notify>(() => {});

export function useNotify(): Notify {
  return useContext(NoticeContext);
}

/**
 * Short messages after an action ("Round 2 paired", "Could not ..."). Info
 * messages are announced politely; errors are announced at once and stay
 * longer.
 */
export function NoticeProvider({ children }: { children: ReactNode }) {
  const [notices, setNotices] = useState<Notice[]>([]);
  const next = useRef(1);

  const dismiss = useCallback((id: number) => {
    setNotices((list) => list.filter((n) => n.id !== id));
  }, []);

  const notify = useCallback<Notify>(
    (text, kind = 'info') => {
      const id = next.current++;
      setNotices((list) => [...list.slice(-2), { id, kind, text }]);
      setTimeout(() => dismiss(id), kind === 'error' ? 12_000 : 5_000);
    },
    [dismiss],
  );

  const info = notices.filter((n) => n.kind === 'info');
  const errors = notices.filter((n) => n.kind === 'error');

  return (
    <NoticeContext.Provider value={notify}>
      {children}
      <div className="notices no-print">
        <div role="status" aria-live="polite">
          {info.map((n) => (
            <NoticeItem key={n.id} notice={n} onDismiss={dismiss} />
          ))}
        </div>
        <div role="alert">
          {errors.map((n) => (
            <NoticeItem key={n.id} notice={n} onDismiss={dismiss} />
          ))}
        </div>
      </div>
    </NoticeContext.Provider>
  );
}

function NoticeItem({ notice, onDismiss }: { notice: Notice; onDismiss: (id: number) => void }) {
  return (
    <div className={`notice notice-${notice.kind}`}>
      <span>{notice.text}</span>
      <button
        type="button"
        className="notice-close"
        aria-label="Dismiss message"
        onClick={() => onDismiss(notice.id)}
      >
        ×
      </button>
    </div>
  );
}
